import { useEffect } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useMigration } from '@/lib/migration/useMigration'
import { assetRows, blocksOfAnnex, footerText, partARows, systemRows } from '@/lib/migration/export'
import { ANNEX_SECTIONS, CHALLENGE_COLUMNS } from '@/lib/migration/engine'
import { section3Text } from '@/api/types/migration'
import { Icon } from '@/components/readiness/primitives'
import { formatDate } from '@/components/common/format'

// Print-ready submission plan (I3 Note 5): /readiness/submission/versions/
// {id}/print renders a saved version's snapshot in CSA's layout through the
// I2 §10 print stylesheet (light theme, no app chrome, repeating footer).
// Rendered from the version row, never from the live plan (§5.2).

export default function PlanPrint() {
  const { versionId = '' } = useParams()
  const [params] = useSearchParams()
  const ctx = useMigration()
  const { index, indexId, plan: data } = ctx
  const version = data.versions.find((v) => v.id === versionId)
  const loaded = !!data.plan && !data.loading

  useEffect(() => {
    document.body.classList.add('rd-print')
    const t = params.get('print') === '1' && version ? window.setTimeout(() => window.print(), 600) : null
    return () => {
      document.body.classList.remove('rd-print')
      if (t) window.clearTimeout(t)
    }
  }, [params, version])

  if (loaded && !version) return <Navigate to={`/readiness/submission/plan?index=${indexId}`} replace />
  if (!version) return null
  const p = version.snapshot.plan
  const annexes = [...version.snapshot.annexes].sort((a, b) => a.sort_order - b.sort_order)
  const footer = footerText(index, version)
  const row = (k: string, v: string) => (
    <div key={k} className="grid gap-x-4 py-1 text-[12px] sm:grid-cols-[200px_1fr]" style={{ borderBottom: '1px solid var(--rd-border-subtle)' }}>
      <dt style={{ color: 'var(--rd-text-muted)' }}>{k}</dt>
      <dd className="whitespace-pre-wrap" style={{ color: v ? 'var(--rd-text-primary)' : 'var(--rd-text-faint)' }}>{v || '—'}</dd>
    </div>
  )

  return (
    <div id="rd-report" className="rd -m-6 min-h-screen p-8" style={{ background: 'var(--rd-bg-page)' }} data-testid="plan-print">
      <div className="rd-no-print mb-4 flex flex-wrap items-center gap-2">
        <Link to={`/readiness/submission/plan?index=${indexId}`} className="rd-link inline-flex items-center gap-1 text-[13px]"><Icon name="arrow-left" size={14} /> Submission plan</Link>
        <button type="button" className="rd-btn rd-btn-primary ml-auto" onClick={() => window.print()}><Icon name="download" size={14} /> Save as PDF</button>
      </div>

      <section className="rd-card p-6">
        <p className="text-[11px] italic" style={{ color: 'var(--rd-text-muted)' }}>Cyber Security Agency of Singapore · Quantum-Safe Migration Plan Template</p>
        <h1 className="mt-1 text-[24px] font-semibold">Quantum-Safe Migration Plan</h1>
        <h2 className="mt-4 text-[16px] font-semibold">Part A — Organisational Level</h2>
        <dl className="mt-2">
          {row('Organisation name', p.org_name)}
          {row('CII sector', p.cii_sector)}
          {row('Accountable executive', [p.accountable_exec_name, p.accountable_exec_title].filter(Boolean).join(', '))}
          {row('Point(s) of contact', p.contacts.map((c) => [c.name, c.title, c.email].filter(Boolean).join(' · ')).join('; '))}
          {row('Submission date', formatDate(version.submission_date))}
          {row('Version', version.label)}
          {version.note?.trim() && row('Key changes in this version', version.note.trim())}
        </dl>
        {partARows(version).map((s) => (
          <section key={s.n} className="objective-card mt-4">
            <h3 className="text-[14px] font-semibold">Section {s.n} · {s.title}</h3>
            <p className="text-[11px] italic" style={{ color: 'var(--rd-text-faint)' }}>{s.csa}</p>
            {s.options && <ul className="mt-1 text-[12px]">{s.options.map((o) => <li key={o.label}>{o.ticked ? '☒' : '☐'} {o.label}</li>)}</ul>}
            {s.lines.filter(Boolean).map((l, i) => <p key={i} className="mt-1 whitespace-pre-wrap text-[12px]">{l}</p>)}
            {s.table && (
              <table className="mt-1 w-full text-[12px]">
                <thead><tr style={{ color: 'var(--rd-text-muted)' }}><th className="text-left font-medium">{CHALLENGE_COLUMNS.blocker}</th><th className="text-left font-medium">{CHALLENGE_COLUMNS.support}</th></tr></thead>
                <tbody>{s.table.length ? s.table.map(([b, sup], i) => <tr key={i} className="align-top"><td className="pr-3 py-1">{b}</td><td className="py-1">{sup}</td></tr>) : <tr><td colSpan={2}>—</td></tr>}</tbody>
              </table>
            )}
          </section>
        ))}
      </section>

      {annexes.map((a, i) => (
        <section key={a.id} className="rd-card rd-page-break mt-4 p-6">
          <h2 className="text-[16px] font-semibold">Part B — System Annex {i + 1} of {annexes.length} · {a.system_name || 'Untitled system'}</h2>
          <h3 className="mt-3 text-[13px] font-semibold">Section 1 · {ANNEX_SECTIONS.s1.title}</h3>
          <dl>{systemRows(a).map(([k, v]) => row(k, v))}</dl>
          <h3 className="mt-3 text-[13px] font-semibold">Section 2 · {ANNEX_SECTIONS.s2.title}</h3>
          {blocksOfAnnex(version, a).map((b, k, arr) => (
            <div key={b.id} className="objective-card mt-2">
              <p className="text-[12px] font-semibold">Asset {k + 1} of {arr.length} · {b.asset_model_version || 'Unnamed asset'}</p>
              <dl>{assetRows(b).map(([k, v]) => row(k, v))}</dl>
            </div>
          ))}
          <h3 className="mt-3 text-[13px] font-semibold">Section 3 · {ANNEX_SECTIONS.s3.title}</h3>
          <p className="mt-1 whitespace-pre-line text-[12px]">{section3Text(a) || '—'}</p>
        </section>
      ))}
      <p className="rd-print-footer mt-6 text-[10px]" style={{ color: 'var(--rd-text-faint)' }}>{footer}</p>
    </div>
  )
}
